import React, { useEffect, useState } from "react";
import { useFormattedMessage } from "hooks";

import {
    PRODUCT_CATEGORIES,
    PRODUCT_TYPE,
    SECONDARY_MARKET_PROJECT_STATUS,
    getProductCategoriesTranslatedLabels,
} from "constants/index";
import { getProjectInvestmentStatusLookUp } from "utils/statusLookUp";

import { PROJECT_STATUS } from "constants/status";

import CustomFormatNumber from "components/CustomFormatNumber";
import PropertyItemProto from "components/PropertyItemProto";
import StatusRibbon from "components/PropertyItemProto/components/StatusRibbon";
import MidRow from "components/PropertyItemProto/components/MidRow";
import FooterDiv from "components/PropertyItemProto/components/FooterDiv";
import FooterPropertyRow from "components/PropertyItemProto/components/FooterPropertyRow";
import EqualProfitContainer from "components/PropertyItemProto/components/EqualProfitContainer";
import IntlFormattedMessage from "./IntlFormattedMessage";

const messages = {
    pricePerShare: "property_price_per_share",
    numberDigitalParts: "number_of_digital_shares",
    financingVolume: "financing_volume",
    totalValue: "property_total_value",
    indicativeWealth: "property_indicative_wealth",
    predictedTotalReturn: "property_predicted_total_return",
    predictedTotalReturnSachwert05: "property_predicted_total_return_sachwert_05",
    numberOfInvestments: "number_of_investments",
    tokenPrice: "assets_token_price",
    assetfooterNote: "property_asset_footer_note",
    realClubfooterNote: "property_real_club_footer_note",
    privateEquityFooterNote: "property_private_equity_footernote",
    indicativeTokenValue: "property_indicative_token_value",
    performance: "meta_title_performance_page",
    propertyTotalReturn: "property_total_return",
    propertyPredictedTotalReturn: "property_predicted_totalreturn",
    propertyDuration: "property_duration",
    propertyRealizedTotalReturn: "property_realized_total_return",
    averageCapitalReturn: "average_capital_return",
    exitProceeds: "exit_proceeds",
    startOfTerm: "start_of_term",
    endOfTerm: "end_of_term",
    nextPayout: "sm_next_payout",
};

const PercentValueCom = ({ percentValue, right }) => (
    <>
        {typeof percentValue !== "undefined" ? (
            <div className="d-flex align-end">
                <span className={`text-12 fw-600 mr-2 ${Math.sign(percentValue) === -1 ? "danger-red-color" : "primary-color"}`}>
                    {`${Math.sign(percentValue) === 1 ? "+" : ""}`}
                    <CustomFormatNumber value={percentValue} symbol="%" />
                </span>
                <CustomFormatNumber value={right} />
            </div>
        ) : (
            <CustomFormatNumber value={right} />
        )}
    </>
);

const TokenPriceValueCom = ({ performancePrice, isSuccessfullyPaidBack = false }) => {
    const { formatMessage } = useFormattedMessage();
    return (
        <>
            {
                <div
                    className={`flex justify-between performance-tile ${Math.sign(performancePrice) === -1 ? "down" : "up"} ${isSuccessfullyPaidBack ? "bg-light-green" : ""}`}
                >
                    <span className="text-12 fw-600">
                        {isSuccessfullyPaidBack ? formatMessage(messages.exitProceeds) : formatMessage(messages.performance)}
                    </span>
                    <div className="d-flex align-end">
                        <span
                            className={`text-12 fw-600 ${
                                Math.sign(performancePrice) === -1 ? "danger-red-color" : "primary-dark-color"
                            }`}
                        >
                            {`${Math.sign(performancePrice) === 1 ? "+ " : ""}`}
                            <CustomFormatNumber value={performancePrice} symbol="€" />
                        </span>
                    </div>
                </div>
            }
        </>
    );
};

const PropertyItem = ({
    dataAsset,
    image,
    propertyType,
    propertyCategory,
    location,
    title,
    profit,
    returnEstimate,
    price,
    shares,
    digitalShares,
    digitalSharesSymbol,
    aggregate,
    indicativeWealth,
    fundingPercentage,
    status,
    onClick,
    VIPUser,
    className,
    tileHoverAnimation,
    projectID,
    numberOfInvestments = 0,
    isAssets = false,
    percentValue,
    tokenPrice,
    valuationDate,
    performancePrice,
    buyCheapestPrice,
    sellHighestPrice,
    ITV,
    reference,
    isVideo,
    videoPath,
    isNotLink,
    href,
    actionBox,
    isSuccessfullyPaidBack,
}) => {
    const PRODUCT_CATEGORIES_TRANSLATED_LABELS = getProductCategoriesTranslatedLabels();

    const { formatMessage } = useFormattedMessage();
    const [smTileKPIs, setSmTileKPIs] = useState({});

    const newStatus = VIPUser && status === PROJECT_STATUS.VIP_FUNDING ? PROJECT_STATUS.FUNDING_ACTIVE : status;

    const footerNote = {
        [PRODUCT_CATEGORIES.TRANSPORTATION]: <IntlFormattedMessage id={messages.assetfooterNote} />,
        [PRODUCT_CATEGORIES.FINE_WINE]: <IntlFormattedMessage id={messages.assetfooterNote} />,
        [PRODUCT_CATEGORIES.ART]: <IntlFormattedMessage id={messages.assetfooterNote} />,
        [PRODUCT_CATEGORIES.CLASSIC_CARS]: <IntlFormattedMessage id={messages.assetfooterNote} />,
        [PRODUCT_CATEGORIES.DIAMONDS]: <IntlFormattedMessage id={messages.assetfooterNote} />,
        [PRODUCT_CATEGORIES.LUXURY_WATCHES]: <IntlFormattedMessage id={messages.assetfooterNote} />,
        [PRODUCT_CATEGORIES.MUSICAL_INSTRUMENTS]: <IntlFormattedMessage id={messages.assetfooterNote} />,
        [PRODUCT_CATEGORIES.PRIVATE_EQUITY]: <IntlFormattedMessage id={messages.realClubfooterNote} />, // Change privateEquityFooterNote to realClubfooterNote for ticket FRON-398
        [PRODUCT_CATEGORIES.RESIDENTIAL]: <IntlFormattedMessage id={messages.realClubfooterNote} />,
        [PRODUCT_TYPE.CL_PUBLIC_PLACEMENT]: <IntlFormattedMessage id={messages.realClubfooterNote} />,
        [PRODUCT_TYPE.CL_DIRECT_SELECT]: <IntlFormattedMessage id={messages.realClubfooterNote} />,
    };

    const renderFooterNote = (type, category) => {
        return projectID === "SACHWERT-05" ? (
            <IntlFormattedMessage id={messages.predictedTotalReturn} />
        ) : projectID === "SI-AB-01" ||
          projectID === "CD-PP-SI-AB-01" ||
          projectID === "CD-PP-SI-AB-02" ||
          projectID === "CD-PP-SI-AB-03" ? (
            <IntlFormattedMessage id={messages.assetfooterNote} />
        ) : projectID === "CD-PP-NEU26-01-01" || projectID === "CD-PP-NEU26-01-02" || projectID === "NEU26-01" ? (
            <IntlFormattedMessage id={messages.averageCapitalReturn} />
        ) : (
            footerNote[category] || footerNote[type] || <IntlFormattedMessage id={messages.predictedTotalReturn} />
        );
    };

    const investmentStatusLookup = getProjectInvestmentStatusLookUp(newStatus);

    // Update Secondary Market Tile Profit KPIs based on actionBox data
    useEffect(() => {
        if (!Array.isArray(actionBox?.line)) return;

        const keyMap = {
            Laufzeitbeginn: "startOfTerm",
            "Progn. Laufzeitende": "endOfTerm",
            "Nächste Ausschüttung": "nextPayout",
        };

        const updatedKPIs = {};

        for (const item of actionBox.line) {
            const key = keyMap[item?.left?.trim()];
            if (key) {
                updatedKPIs[key] = item?.right;
            }
        }

        if (Object.keys(updatedKPIs).length > 0) {
            setSmTileKPIs((prev) => ({ ...prev, ...updatedKPIs }));
        }
    }, [actionBox]);

    let performance = ITV && ITV !== price ? ((ITV - price) / price) * 100 : null;

    if (performance !== null) {
        const rounded = Number(performance.toFixed(10)); // Avoid floating-point noise
        performance = Number.isInteger(rounded) ? Math.floor(rounded) : +rounded.toFixed(2);
    }

    return (
        <PropertyItemProto
            isNotLink={isNotLink}
            href={href}
            tileHoverAnimation={tileHoverAnimation}
            image={image}
            imageText={PRODUCT_CATEGORIES_TRANSLATED_LABELS[propertyCategory] || propertyCategory}
            onClick={onClick}
            className={className}
            dataAsset={dataAsset}
            dataStatus={status}
            reference={reference}
            isVideo={isVideo}
            videoPath={videoPath}
        >
            {newStatus && (
                <StatusRibbon
                    {...investmentStatusLookup}
                    projectID={projectID}
                    percentage={
                        newStatus === PROJECT_STATUS.FUNDING_CLOSED || newStatus === PROJECT_STATUS.FINANCING_COMPLETED
                            ? 100
                            : fundingPercentage
                    }
                    buyCheapestPrice={buyCheapestPrice}
                    sellHighestPrice={sellHighestPrice}
                    isProgressBar={investmentStatusLookup?.isProgressBar}
                />
            )}
            {isAssets && (
                <TokenPriceValueCom performancePrice={performancePrice} isSuccessfullyPaidBack={isSuccessfullyPaidBack} />
            )}
            <EqualProfitContainer
                makeContainerEqualHeight
                equalSection={
                    <>
                        {location ? (
                            <span className="text-12 fw-500 property-item-text-overflow my-1" title={location}>
                                {location}
                            </span>
                        ) : null}
                        {title ? (
                            <h6 className="" title={title}>
                                {title}
                            </h6>
                        ) : null}
                    </>
                }
                profit={
                    newStatus === PROJECT_STATUS.SUCCESSFULLY_PAID_BACK ? (
                        <>
                            <MidRow
                                left={formatMessage(messages.propertyTotalReturn)}
                                right={
                                    <CustomFormatNumber
                                        value={
                                            actionBox?.line[1]?.right?.split(",")[0] +
                                            "." +
                                            actionBox?.line[1]?.right?.split(",")[1]
                                        }
                                        symbol="%"
                                    />
                                }
                            />
                            <MidRow
                                left={formatMessage(messages.propertyPredictedTotalReturn)}
                                right={
                                    <CustomFormatNumber
                                        value={
                                            actionBox?.line[2]?.right?.split(",")[0] +
                                            "." +
                                            actionBox?.line[2]?.right?.split(",")[1]
                                        }
                                        symbol="%"
                                    />
                                }
                            />
                            <MidRow left={formatMessage(messages.propertyDuration)} right={actionBox?.line[3]?.right} />
                        </>
                    ) : SECONDARY_MARKET_PROJECT_STATUS.includes(newStatus) ? (
                        <>
                            <MidRow left={formatMessage(messages.startOfTerm)} right={smTileKPIs?.startOfTerm || "----"} />
                            <MidRow left={formatMessage(messages.endOfTerm)} right={smTileKPIs?.endOfTerm || "----"} />
                            <MidRow left={formatMessage(messages.nextPayout)} right={smTileKPIs?.nextPayout || "----"} />
                        </>
                    ) : (
                        <>
                            {isAssets ? (
                                <MidRow left={formatMessage(messages.numberOfInvestments)} right={numberOfInvestments} />
                            ) : null}
                            {!isAssets && price ? (
                                <MidRow
                                    left={formatMessage(messages.pricePerShare)}
                                    right={<CustomFormatNumber value={typeof price === "string" ? parseFloat(price) : price} />}
                                />
                            ) : isAssets ? (
                                <MidRow
                                    left={
                                        <>
                                            {formatMessage(messages.tokenPrice)}{" "}
                                            <span className="another-grey-color">- ITV {valuationDate}</span>
                                        </>
                                    }
                                    right={<PercentValueCom percentValue={percentValue} right={tokenPrice} />}
                                />
                            ) : null}
                            {shares ? (
                                <MidRow
                                    left={formatMessage(messages.numberDigitalParts)}
                                    // right={addGermanDecimal(shares)}
                                    right={<CustomFormatNumber value={shares} fixed={0} noSymbol />}
                                />
                            ) : null}
                            {digitalShares || digitalShares == 0 ? (
                                <MidRow
                                    left={formatMessage(messages.numberDigitalParts)}
                                    // right={addGermanDecimal(digitalShares)}
                                    right={
                                        <CustomFormatNumber
                                            value={digitalShares}
                                            fixed={0}
                                            symbol={digitalSharesSymbol}
                                            noSymbol={digitalSharesSymbol ? false : true}
                                        />
                                    }
                                />
                            ) : null}
                            {profit ? (
                                <MidRow
                                    left={formatMessage(messages.financingVolume)}
                                    right={<CustomFormatNumber value={profit} fixed={0} />}
                                />
                            ) : null}
                            {aggregate ? (
                                <MidRow
                                    left={formatMessage(messages.totalValue)}
                                    right={<CustomFormatNumber className="text-no-break" value={aggregate} fixed={2} />}
                                />
                            ) : null}
                            {isAssets ? (
                                <MidRow
                                    left={formatMessage(messages.indicativeWealth)}
                                    right={<CustomFormatNumber className="text-no-break" value={indicativeWealth} fixed={2} />}
                                />
                            ) : null}
                        </>
                    )
                }
            />
            {returnEstimate ? (
                <FooterDiv>
                    {newStatus === PROJECT_STATUS.SUCCESSFULLY_PAID_BACK ? (
                        <FooterPropertyRow
                            up={formatMessage(messages.propertyRealizedTotalReturn)}
                            down={actionBox?.line[0]?.right?.split(",")[0] + "." + actionBox?.line[0]?.right?.split(",")[1]}
                            symbol="%"
                        />
                    ) : SECONDARY_MARKET_PROJECT_STATUS.includes(newStatus) ? (
                        <FooterPropertyRow
                            up={<IntlFormattedMessage id={messages.indicativeTokenValue} />}
                            down={ITV || 0}
                            symbol="€"
                            performance={performance}
                        />
                    ) : (
                        <FooterPropertyRow
                            up={renderFooterNote(propertyType, propertyCategory)}
                            down={returnEstimate}
                            symbol={"%"}
                        />
                    )}
                </FooterDiv>
            ) : null}
        </PropertyItemProto>
    );
};
const PropertyItemComponent = React.forwardRef(PropertyItem);
export default PropertyItemComponent;
